/**
 * Client-side validation for the Lecture Library Forms
 */

// Helper to check if a value is empty (null, undefined, empty string)
const isEmpty = (value) => {
  return value === undefined || value === null || String(value).trim() === "";
};

// URL validation regex (accepts absolute URLs and standard keys if uploading locally)
const isValidUrl = (url) => {
  if (isEmpty(url)) return false;
  // If it's a file key (e.g. R2 keys with slashes, alphanumeric, hyphens, underscores, dots)
  if (/^[a-zA-Z0-9_\-\.\/]+$/.test(url)) return true; // Local R2 Key (supports path separators)
  try {
    new URL(url);
    return true;
  } catch (_) {
    return false;
  }
};

/**
 * Validates the Lecture Video form data
 * @param {Object} data - Form data fields
 * @returns {Object} - Object containing field errors, if any
 */
export const validateLectureForm = (data) => {
  const errors = {};

  if (isEmpty(data.branch_id)) {
    errors.branch_id = "Please select a Branch";
  }

  if (isEmpty(data.video_category_id)) {
    errors.video_category_id = "Please select a Category";
  }

  if (isEmpty(data.video_sub_category_id)) {
    errors.video_sub_category_id = "Please select a Sub Category";
  }

  if (isEmpty(data.lecturer_id)) {
    errors.lecturer_id = "Please select a Lecturer";
  }

  if (isEmpty(data.class_id)) {
    errors.class_id = "Please select a Class";
  }

  if (isEmpty(data.subject_id)) {
    errors.subject_id = "Please select a Subject";
  }

  if (isEmpty(data.title)) {
    errors.title = "Title is required";
  } else if (data.title.trim().length > 100) {
    errors.title = "Title cannot exceed 100 characters";
  }

  if (isEmpty(data.video_url)) {
    errors.video_url = "Video is required";
  } else if (!isValidUrl(data.video_url)) {
    errors.video_url = "Please provide a valid video URL or R2 storage key";
  }

  if (isEmpty(data.video_date)) {
    errors.video_date = "Video date is required";
  }

  if (data.description && data.description.length > 1500) {
    errors.description = "Description cannot exceed 1500 characters";
  }

  return errors;
};

/**
 * Validates the Category form data
 * @param {Object} data - Category fields
 * @returns {Object} - Object containing field errors, if any
 */
export const validateCategoryForm = (data) => {
  const errors = {};

  if (isEmpty(data.video_category)) {
    errors.video_category = "Category name is required";
  } else if (data.video_category.trim().length < 2) {
    errors.video_category = "Category name must be at least 2 characters";
  } else if (data.video_category.length > 45) {
    errors.video_category = "Category name cannot exceed 45 characters";
  }

  return errors;
};

/**
 * Validates the Sub Category form data
 * @param {Object} data - Subcategory fields
 * @returns {Object} - Object containing field errors, if any
 */
export const validateSubCategoryForm = (data) => {
  const errors = {};

  if (isEmpty(data.video_sub_category)) {
    errors.video_sub_category = "Sub Category name is required";
  } else if (data.video_sub_category.trim().length < 2) {
    errors.video_sub_category = "Sub Category name must be at least 2 characters";
  } else if (data.video_sub_category.length > 45) {
    errors.video_sub_category = "Sub Category name cannot exceed 45 characters";
  }

  if (isEmpty(data.video_category_id)) {
    errors.video_category_id = "Category mapping is required";
  }

  if (data.description && data.description.length > 200) {
    errors.description = "Description cannot exceed 200 characters";
  }

  return errors;
};
